import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { formatMessage } from 'devextreme/localization';
import LoadPanel from 'devextreme-react/load-panel';

import { apiScopes, ssoAuthority } from '../../AuthConfig';
import SsoApi from '../../api/SsoApi';

import '../login-page/LoginPage.css';

interface SsoLoginPageProps {
    // Segment of the company sign-in URL (/sso/{loginSlug}).
    loginSlug: string;
}

type SsoLoginState = 'redirecting' | 'unknown' | 'failed';

/**
 * Sign-in page of a company that uses its own identity provider (SAML SSO), reached through the URL of the company.
 * It never falls back to the regular sign-in: an SSO account and a regular account of the same person are separate.
 */
const SsoLoginPage: React.FC<SsoLoginPageProps> = ({ loginSlug }) => {
    const { instance } = useMsal();

    const [state, setState] = useState<SsoLoginState>('redirecting');

    useEffect(() => {
        let cancelled = false;

        const start = async () => {
            try {
                if (!ssoAuthority) {
                    setState('unknown');
                    return;
                }

                const resolution = await SsoApi.ResolveByLoginSlugAsync(loginSlug);

                if (cancelled) {
                    return;
                }

                if (resolution.IsFailed()) {
                    setState('failed');
                    return;
                }

                if (!resolution.Result.IsSso) {
                    setState('unknown');
                    return;
                }

                await instance.loginRedirect({
                    scopes: apiScopes,
                    authority: ssoAuthority,
                    // MSAL returns to the page that started the sign-in: that is /sso/{slug}, which must not be the
                    // address of the application once the user is signed in.
                    redirectStartPage: window.location.origin + '/',
                    extraQueryParameters: { domain_hint: resolution.Result.DomainHint }
                });
            } catch (error) {
                console.error(error);

                if (!cancelled) {
                    setState('failed');
                }
            }
        };

        start();

        return () => {
            cancelled = true;
        };
    }, [instance, loginSlug]);

    if (state === 'redirecting') {
        return <LoadPanel visible={true} />;
    }

    return (
        <div className="login-page">
            <div className="login-page-card">
                <h2>{formatMessage('SsoLoginPage_Title')}</h2>
                <div className="login-page-error" role="alert">
                    {formatMessage(state === 'unknown' ? 'SsoLoginPage_Unknown' : 'SsoLoginPage_Failed')}
                </div>
                <a href={window.location.origin + '/'}>{formatMessage('SsoLoginPage_RegularSignIn')}</a>
            </div>
        </div>
    );
};

export default SsoLoginPage;
